/* Brain2Canvas, PaintFX (b2c-paintfx.js)
   =========================================================================
   ERZAEHL-SATZ je Moment (93 Design-Team-Arbeitsweise A.2 / V40, ohne Satz
   wird nicht gebaut):

     1. Sprühspur auf der Creme-Fläche  ->  „Der Besucher hinterlässt selbst Farbe."
     2. Drip beim Verweilen             ->  „Wer stehen bleibt, sprüht zu viel, die Farbe läuft."
     3. Spur auf der dunklen Wand       ->  „Auch auf der Wand bleibt eine Spur, aber leiser."

   Der Zeiger zieht einen weichen Periwinkle-Sprühkreis über die Fläche und
   lässt eine Spur zurück, die in rund zwei Sekunden weg trocknet. Bleibt der
   Zeiger stehen, wird der Kern nass und ein Tropfen läuft ein Stück ab.

   Herkunft: Formsprache aus `design-system-v3-import/concept/paintfx.js`
   (DOSSIER C.4, Zeile „Spray-Cursor (PaintFX)"). Das ist eine FORMSPRACHE-
   Quelle, keine Code-Quelle (DOSSIER CB.1), deshalb neu gebaut. Die vier
   inhaltlichen Unterschiede zur Vorlage stehen unten unter „Was anders ist".

   HARTE GRENZEN (STYLE-KIT §0/§3, DOSSIER CB.5, Auftrag):
   - Nur `pointer:fine` UND Viewport >= 960px. Touch bekommt den Effekt nie.
     Ändert sich das (Resize, Stift->Finger), baut sich der Effekt selbst ab.
   - `prefers-reduced-motion: reduce`  ->  gar nicht erst gemountet. Der Inhalt
     ist vollständig und unverändert, es fehlt nur die Farbe.
   - Kein Scroll-Listener. Einziger Eingang ist `pointermove` (passive).
   - Kein Layout wird angefasst: kein `overflow`, keine Größe, keine Position
     außer `position:relative`, wenn das Element `static` ist. Der Canvas liegt
     `position:absolute; inset:0; z-index:-1; pointer-events:none` und damit
     hinter jedem Inhalt und unter keinem Bedienelement.
   - `IntersectionObserver` pausiert off-screen, Leerlauf stoppt den rAF.

   Was anders ist als in der Concept-Vorlage (bewusst, jeweils mit Grund):
   1. **Heilen per `destination-out` statt Hintergrundfarbe.** Die Vorlage malt
      jeden Frame die Sektionsfarbe mit 5 % darüber. Das setzt voraus, dass die
      Farbe exakt getroffen wird, und hinterlässt auf Verläufen/Bildern einen
      sichtbaren Fleck. Hier wird stattdessen Alpha abgetragen: der Canvas
      bleibt überall transparent, wo nichts gesprüht wurde. Damit funktioniert
      der Effekt auch über dem Mesh-Verlauf und über Bildern.
   2. **Nur der benutzte Bereich wird geheilt** (Dirty-Rect statt Vollfläche).
      Auf der 2.400 px hohen Karten-Wand spart das den Löwenanteil der Füllrate.
   3. **Kein Auto-Scan über alle `section`.** Die Vorlage hängt sich an jede
      einfarbige Sektion. Hier gibt es eine benannte Liste (MOUNTS) plus
      `data-paintfx`, der Effekt erscheint nur dort, wo er erzählt wurde.
   4. **Backing-Store gedeckelt** (MAX_PX). Ein 1240x3000-Element würde sonst
      bei DPR 2 rund 60 MB Bildspeicher belegen.
   ========================================================================= */
(function () {
  'use strict';

  /* --- Wo der Effekt lebt. Reihenfolge egal, doppelte Treffer sind geschützt.
         `soft` = halbe Deckkraft, für die dunklen Flächen (Erzähl-Satz 3). --- */
  // Je Eintrag beide Welten: die Liquid-Section (`#b2c-ref-root`) und der
  // lokale Prototyp (`[data-page="index"]`), damit Abnahme-Render und
  // Live-Seite denselben Effekt zeigen und nicht nur einer von beiden.
  //
  // Die Karten-Wand (`.wrap--wall`) stand hier in der ersten Runde und ist
  // wieder raus: sie ist zu 90 % von Karten mit eigener Flaeche verdeckt, der
  // Effekt lief dort messbar (33.000 gesetzte Pixel) und war im Frame
  // trotzdem nicht zu sehen. Ein Canvas ueber 1022x2154 px, der nichts zeigt,
  // ist reine Fuellrate. Statt dessen die Periwinkle-Kachel, das ist auch die
  // Flaeche, die der Owner-Referenzframe (_review/paint-demo.png) zeigt.
  var MOUNTS = [
    { sel: '#b2c-ref-root .clients, [data-page="index"] .clients',
      variant: 'full' },                                     // Creme-Band
    { sel: '#b2c-ref-root .ctatile, [data-page="index"] .ctatile',
      variant: 'full' },                                     // Periwinkle-Kachel
    // Case-Seiten: die ganze dunkle Flaeche, nicht die Lesespalte. In Runde 1
    // hing der Effekt an `.b2c-body` und `.b2c-related`, beide sind von
    // Fliesstext, Galeriebildern und Karten zugedeckt, der Canvas liegt
    // dahinter und war im Frame nicht zu sehen. Sichtbar ist auf einer
    // Case-Seite das Wandschwarz NEBEN der Spalte; genau dort faehrt die Maus.
    { sel: '#b2c-case',                  variant: 'soft' },  // Case v2
    { sel: '[data-b2c-ref="case"]',      variant: 'soft' }   // Case-Section (live)
  ];

  /* --- Grenzwerte ------------------------------------------------------- */
  var MIN_W      = 960;        // Viewport-Untergrenze (STYLE-KIT §3)
  var MAX_PX     = 2200000;    // Deckel für den Canvas-Backing-Store
  var MAX_DRIPS  = 10;         // Partikel-Deckel: gleichzeitige Tropfen
  var SPRAY_MAX  = 14;         // Partikel-Deckel: Nebelpunkte je Frame
  var HEAL       = 0.04;       // Alpha-Abtrag je Frame -> Spur trocknet in ~2 s
  var FADE_FRAMES= 130;        // danach ist auch der letzte Rest weg
  var IDLE_STOP  = 130;        // Frames ohne Zeiger, dann schläft der rAF

  var MQ_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');
  var MQ_FINE   = window.matchMedia('(pointer:fine)');
  var MQ_WIDE   = window.matchMedia('(min-width:' + MIN_W + 'px)');

  function allowed() {
    return !MQ_MOTION.matches && MQ_FINE.matches && MQ_WIDE.matches;
  }

  /* --- Farbe ------------------------------------------------------------ */
  function hexToRgb(h) {
    h = String(h || '').trim().replace('#', '');
    if (h.length === 3) h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
    var n = parseInt(h, 16);
    if (isNaN(n)) return [157, 161, 202];
    return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
  }
  function parseColor(str) {
    if (!str) return null;
    str = String(str).trim();
    if (str[0] === '#') { var c = hexToRgb(str); return [c[0], c[1], c[2], 1]; }
    var m = str.match(/rgba?\(([^)]+)\)/i);
    if (!m) return null;
    var p = m[1].split(/[,\s/]+/).filter(Boolean).map(parseFloat);
    return [p[0] | 0, p[1] | 0, p[2] | 0, p.length > 3 ? p[3] : 1];
  }
  function luminance(rgb) {   // grobe Helligkeit, reicht für hell/dunkel
    return (0.2126 * rgb[0] + 0.7152 * rgb[1] + 0.0722 * rgb[2]) / 255;
  }
  /* Erste deckende Hintergrundfarbe im Vorfahren-Pfad. Nur zur Entscheidung
     hell/dunkel, gemalt wird nie mit dieser Farbe (siehe Unterschied 1). */
  function surfaceColor(el) {
    for (var n = el; n && n.nodeType === 1; n = n.parentElement) {
      var c = parseColor(getComputedStyle(n).backgroundColor);
      if (c && c[3] >= 0.9) return c;
    }
    return [11, 11, 13, 1];   // ink-deep: die Seite ist im Zweifel dunkel
  }
  /* Token-Wahrheit ist die CSS-Schicht (F-006). Der Literal ist nur der
     Notnagel, wenn die Variable auf dieser Seite nicht definiert ist. */
  function token(el, name, fallback) {
    var v = getComputedStyle(el).getPropertyValue(name);
    v = v && v.trim();
    return v || fallback;
  }
  function palette(el, light) {
    var peri     = token(el, '--peri',        '#9da1ca');
    var periMid  = token(el, '--peri-mid',    '#8b91c4');
    var periDeep = token(el, '--peri-deep',   '#686fae');
    var periInk  = token(el, '--peri-ink',    '#7b82bd');
    var mauve    = token(el, '--mauve',       '#ecd9de');
    // Nur die Periwinkle-Familie. In der ersten Runde lief `--yellow-deep`
    // mit; beim Verweilen sammelte sich die Farbe genau dort und die Spur
    // wurde ockergelb. Der Owner-Wunsch ist ein Periwinkle-Sprühkreis, kein
    // Farbkarussell, Gelb bleibt Signalfarbe der Buttons.
    return (light ? [periInk, periDeep, periMid, periDeep]
                  : [peri, periMid, mauve, periInk]).map(hexToRgb);
  }

  /* --- Eine Instanz ----------------------------------------------------- */
  function PaintFX(el, opts) {
    opts = opts || {};
    if (!el || el.__b2cPaint) return null;

    var soft    = opts.variant === 'soft';
    var mounted = false;
    var canvas, ctx, scale = 1, W = 0, H = 0;
    var pal, phase = 0, light = true;
    var target = null, brush = null, drips = [];
    var dirty = null, sinceStamp = 1e9, idle = 0, dwell = 0;
    var raf = 0, running = false, visible = true;
    var ro = null, io = null;

    var MAXW = 34, MINW = 7;
    var CORE = 0.22, SPRAY = 12;
    // Deckkraft wird beim Mounten nach der Flaeche gesetzt: auf Wandschwarz
    // braucht dieselbe Farbe MEHR Alpha, um gleich leise zu wirken. Runde 1
    // hatte die dunklen Flaechen mit halbem Alpha gebaut, messbar da,
    // im Frame unsichtbar. „Dezent" ist ein Ergebnis, kein Zahlenwert.

    var api = { mount: mount, unmount: unmount, el: el };
    el.__b2cPaint = api;

    /* --- Auf-/Abbau --- */
    function mount() {
      if (mounted) return;
      mounted = true;

      var cs = getComputedStyle(el);
      if (cs.position === 'static') el.style.position = 'relative';
      // eigener Stapel-Kontext, damit z-index:-1 hinter dem Inhalt landet
      // und nicht hinter der eigenen Hintergrundfarbe verschwindet
      el.style.isolation = 'isolate';

      canvas = document.createElement('canvas');
      canvas.className = 'b2c-paintfx';
      canvas.setAttribute('aria-hidden', 'true');
      ctx = canvas.getContext('2d');
      el.insertBefore(canvas, el.firstChild);

      light = luminance(surfaceColor(el)) > 0.5;
      pal = palette(el, light);
      CORE = (light ? 0.26 : 0.26) * (soft ? 0.78 : 1);
      SPRAY = light ? 12 : 11;
      resize();

      if ('ResizeObserver' in window) {
        ro = new ResizeObserver(resize);
        ro.observe(el);
      } else {
        window.addEventListener('resize', resize, { passive: true });
      }
      if ('IntersectionObserver' in window) {
        io = new IntersectionObserver(onIntersect, { threshold: 0.02 });
        io.observe(el);
      }
      el.addEventListener('pointermove', onMove, { passive: true });
      el.addEventListener('pointerleave', onLeave, { passive: true });

      requestAnimationFrame(function () { canvas.classList.add('is-on'); });
    }

    function unmount() {
      if (!mounted) return;
      mounted = false;
      stop();
      el.removeEventListener('pointermove', onMove);
      el.removeEventListener('pointerleave', onLeave);
      if (ro) { ro.disconnect(); ro = null; }
      else window.removeEventListener('resize', resize);
      if (io) { io.disconnect(); io = null; }
      if (canvas && canvas.parentNode) canvas.parentNode.removeChild(canvas);
      canvas = null; ctx = null;
      el.style.isolation = '';
      target = null; brush = null; drips = []; dirty = null;
    }

    function onIntersect(entries) {
      for (var i = 0; i < entries.length; i++) visible = entries[i].isIntersecting;
      if (visible) { if (dirty) start(); } else stop();
    }

    function resize() {
      if (!canvas) return;
      var r = el.getBoundingClientRect();
      W = Math.max(1, Math.round(r.width));
      H = Math.max(1, Math.round(r.height));
      // Backing-Store deckeln: die Spur ist weich, Auflösungsverlust sieht
      // man ihr nicht an, 60 MB Bildspeicher merkt der Rechner sehr wohl.
      scale = Math.min(window.devicePixelRatio || 1, 1.5);
      var px = W * H * scale * scale;
      if (px > MAX_PX) scale = Math.max(0.5, scale * Math.sqrt(MAX_PX / px));
      canvas.width  = Math.max(1, Math.round(W * scale));
      canvas.height = Math.max(1, Math.round(H * scale));
      ctx.setTransform(scale, 0, 0, scale, 0, 0);
      dirty = null;               // alter Inhalt ist nach dem Skalieren wertlos
      brush = null; target = null; drips = [];
    }

    /* --- Eingabe --- */
    function onMove(e) {
      if (e.pointerType === 'touch') return;   // Gürtel und Hosenträger
      var r = canvas.getBoundingClientRect();
      target = { x: e.clientX - r.left, y: e.clientY - r.top };
      if (!brush) brush = { x: target.x, y: target.y };
      idle = 0;
      start();
    }
    function onLeave() { target = null; brush = null; }

    /* --- Dirty-Rect --- */
    function mark(x, y, r) {
      var x0 = x - r, y0 = y - r, x1 = x + r, y1 = y + r;
      if (!dirty) dirty = { x0: x0, y0: y0, x1: x1, y1: y1 };
      else {
        if (x0 < dirty.x0) dirty.x0 = x0;
        if (y0 < dirty.y0) dirty.y0 = y0;
        if (x1 > dirty.x1) dirty.x1 = x1;
        if (y1 > dirty.y1) dirty.y1 = y1;
      }
      sinceStamp = 0;
    }

    /* --- Schleife --- */
    function frame() {
      if (!ctx) return;

      // 1. Trocknen: Alpha im benutzten Bereich abtragen.
      if (dirty) {
        var x = Math.max(0, dirty.x0), y = Math.max(0, dirty.y0);
        var w = Math.min(W, dirty.x1) - x, h = Math.min(H, dirty.y1) - y;
        if (w > 0 && h > 0) {
          ctx.globalCompositeOperation = 'destination-out';
          ctx.fillStyle = 'rgba(0,0,0,' + HEAL + ')';
          ctx.fillRect(x, y, w, h);
        }
        ctx.globalCompositeOperation = 'source-over';
      }

      // 2. Tropfen laufen ab (Erzähl-Satz 2).
      for (var d = drips.length - 1; d >= 0; d--) {
        var t = drips[d], ny = t.y + t.vy;
        ctx.strokeStyle = 'rgba(' + t.col + ',' + (soft ? 0.3 : 0.4) + ')';
        ctx.lineWidth = t.w; ctx.lineCap = 'round';
        ctx.beginPath(); ctx.moveTo(t.x, t.y); ctx.lineTo(t.x, ny); ctx.stroke();
        mark(t.x, ny, t.w + 2);
        t.y = ny; t.vy = Math.min(1.7, t.vy + 0.035); t.life += t.vy;
        if (t.life >= t.max || t.y > H + 20) drips.splice(d, 1);
      }

      // 3. Sprühen.
      if (brush && target) {
        var px = brush.x, py = brush.y;
        brush.x += (target.x - brush.x) * 0.2;
        brush.y += (target.y - brush.y) * 0.2;
        var dx = brush.x - px, dy = brush.y - py;
        var sp = Math.sqrt(dx * dx + dy * dy);

        // langsam = fetter Kern, schnell = dünne Linie
        var bw = Math.max(MINW, MAXW - sp * 0.95);
        var fastness = sp / MAXW;
        phase += 0.026 + sp * 0.0024;
        var c = colorAt(phase), rgb = c[0] + ',' + c[1] + ',' + c[2];

        // Saettigungs-Bremse: steht der Zeiger, stapelt sich sonst Frame um
        // Frame derselbe Kreis auf denselben Punkt, bis ein deckender Ball
        // dasteht. Der Kern soll nass werden, nicht zum Klecks wachsen.
        if (sp < 0.6) dwell++; else dwell = 0;
        var damp = Math.max(0.3, 1 / (1 + dwell * 0.05));

        // Beim Verweilen zittert die Dose. Ohne dieses Zittern legt sich Frame
        // um Frame derselbe Verlauf auf denselben Punkt und es entstehen
        // konzentrische Ringe wie auf einer Zielscheibe statt eines Flecks.
        // Position UND Radius zittern, sonst legt sich derselbe Verlauf mit
        // derselben Kante immer wieder deckungsgleich uebereinander und die
        // Kante des Mittelstopps wird als Ring sichtbar (Zielscheibe).
        var cx = brush.x, cy = brush.y, cr = bw;
        if (dwell) {
          cx += (Math.random() - 0.5) * 6;
          cy += (Math.random() - 0.5) * 6;
          cr = bw * (0.88 + Math.random() * 0.24);
        }

        var coreA = Math.min(CORE, Math.max(CORE * 0.22, (bw / MAXW) * CORE * 1.6)) * damp;
        var g = ctx.createRadialGradient(cx, cy, 0, cx, cy, cr);
        g.addColorStop(0, 'rgba(' + rgb + ',' + coreA + ')');
        // Mittelstopp: ohne ihn zerlaeuft der Kern zum Nebelfleck, mit ihm
        // bleibt der runde Punkt stehen, den der Referenzframe zeigt.
        g.addColorStop(0.35, 'rgba(' + rgb + ',' + (coreA * 0.62) + ')');
        g.addColorStop(0.7, 'rgba(' + rgb + ',' + (coreA * 0.22) + ')');
        g.addColorStop(1, 'rgba(' + rgb + ',0)');
        ctx.fillStyle = g;
        ctx.beginPath(); ctx.arc(cx, cy, cr, 0, 6.283); ctx.fill();

        // dünne Verbindung, damit schnelle Striche nicht zu Punktketten zerfallen
        ctx.strokeStyle = 'rgba(' + rgb + ',' +
          (0.05 + Math.min(0.24, fastness * 0.42)) * (soft ? 0.8 : 1) + ')';
        ctx.lineWidth = Math.max(1.2, bw * 0.38);
        ctx.lineCap = 'round'; ctx.lineJoin = 'round';
        ctx.beginPath(); ctx.moveTo(px, py); ctx.lineTo(brush.x, brush.y); ctx.stroke();

        // Sprühnebel: beim Verweilen breiter, aber nicht dichter (siehe damp)
        var spread = Math.max(4, bw * 1.35);
        var n = Math.min(SPRAY_MAX, Math.round(SPRAY * damp));
        for (var k = 0; k < n; k++) {
          var ang = Math.random() * 6.283;
          var rr = Math.pow(Math.random(), 0.7) * spread;
          ctx.fillStyle = 'rgba(' + rgb + ',' +
            (0.03 + Math.random() * 0.1) * (soft ? 0.8 : 1) + ')';
          ctx.beginPath();
          ctx.arc(brush.x + Math.cos(ang) * rr, brush.y + Math.sin(ang) * rr,
                  Math.random() * 1.4 + 0.5, 0, 6.283);
          ctx.fill();
        }

        // Tropfen nur beim Verweilen, gedeckelt
        if (sp < 1.0 && bw > MAXW * 0.62 && drips.length < MAX_DRIPS &&
            Math.random() < 0.09) {
          drips.push({ x: brush.x, y: brush.y + bw * 0.5, vy: 0.3,
                       w: Math.max(1.3, bw * 0.15), col: rgb,
                       life: 0, max: 16 + Math.random() * 40 });
        }

        mark(brush.x, brush.y, spread + bw);
        idle = 0;
      } else {
        idle++;
      }

      sinceStamp++;
      if (sinceStamp > FADE_FRAMES && !drips.length) {
        // alles getrocknet: Fläche einmal hart leeren, dann schlafen
        if (dirty) { ctx.clearRect(0, 0, W, H); dirty = null; }
        if (idle > IDLE_STOP) { stop(); return; }
      }
      if (!visible) { stop(); return; }
      raf = requestAnimationFrame(frame);
    }

    function colorAt(p) {
      var n = pal.length;
      var i = ((Math.floor(p) % n) + n) % n, j = (i + 1) % n, f = p - Math.floor(p);
      var a = pal[i], b = pal[j];
      return [Math.round(a[0] + (b[0] - a[0]) * f),
              Math.round(a[1] + (b[1] - a[1]) * f),
              Math.round(a[2] + (b[2] - a[2]) * f)];
    }

    function start() {
      if (running || !visible || !mounted) return;
      running = true; idle = 0;
      raf = requestAnimationFrame(frame);
    }
    function stop() {
      running = false;
      if (raf) cancelAnimationFrame(raf);
      raf = 0;
    }

    if (allowed()) mount();
    return api;
  }

  /* --- Registrierung + Umgebungs-Wechsel -------------------------------- */
  var instances = [];

  function collect() {
    var seen = [];
    var out = [];
    function add(el, variant) {
      if (!el || seen.indexOf(el) !== -1) return;
      seen.push(el);
      out.push({ el: el, variant: variant });
    }
    document.querySelectorAll('[data-paintfx]').forEach(function (el) {
      add(el, el.getAttribute('data-paintfx') === 'soft' ? 'soft' : 'full');
    });
    MOUNTS.forEach(function (m) {
      document.querySelectorAll(m.sel).forEach(function (el) { add(el, m.variant); });
    });
    return out;
  }

  function boot() {
    collect().forEach(function (m) {
      var inst = m.el.__b2cPaint || PaintFX(m.el, { variant: m.variant });
      if (inst && instances.indexOf(inst) === -1) instances.push(inst);
    });
  }

  // Wechselt die Umgebung (Fenster verkleinert, Reduced-Motion eingeschaltet,
  // Maus abgezogen), baut sich der Effekt ab oder wieder auf. Ein Effekt, der
  // nur beim Laden prüft, überlebt genau bis zum ersten Resize.
  function onEnv() {
    var ok = allowed();
    instances.forEach(function (i) { ok ? i.mount() : i.unmount(); });
    if (ok) boot();
  }
  [MQ_MOTION, MQ_FINE, MQ_WIDE].forEach(function (mq) {
    if (mq.addEventListener) mq.addEventListener('change', onEnv);
    else if (mq.addListener) mq.addListener(onEnv);
  });

  window.B2CPaintFX = function (el, opts) { return PaintFX(el, opts || {}); };
  window.B2CPaintFX.boot = boot;

  // Dreimal booten, weil nicht jede Traeger-Flaeche im Rohdokument steht:
  // im Prototyp haengt `clients.js` das Creme-Band erst nach dem Laden ein.
  // `boot` ist idempotent (jedes Element traegt seine Instanz an `__b2cPaint`),
  // ein Dauer-MutationObserver waere dafuer zu teuer.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  window.addEventListener('load', boot);
  setTimeout(boot, 1400);
})();
